import { GhostButton, GridItem, Text } from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { useField } from "formik";
import React, { useContext, useState } from "react";
import { Trans } from "react-i18next";

import { EmployerReferenceList } from "./EmployerReferenceList";
import { EmployerReferenceModal } from "./EmployerReferenceModal";
import { ApplicationContext } from "../../Application.context";
import { getValidationRule } from "../../validationRules";
import { Rules } from "../../validationRules/types";

type EmploymentReferencesCheckField =
  components["schemas"]["EmploymentReferencesCheckField"];

export function EmployerReferenceInputField({
  field: config,
}: {
  field: EmploymentReferencesCheckField;
}) {
  const { saveData } = useContext(ApplicationContext);
  const [field, , helpers] = useField<EmploymentReferencesCheckField["value"]>(
    config.name
  );
  const [employment_references, setEmploymentReferences] = useState<
    components["schemas"]["EmploymentReferencesCheckFieldValue"]
  >(JSON.parse(JSON.stringify(field.value)));

  const maxReferencesRule = getValidationRule(
    config,
    Rules.MAXIMUM_EMPLOYMENT_REFERENCES
  );
  // if the rule isn't present then it's always valid
  const isMaxReferencesRuleValid = maxReferencesRule
    ? maxReferencesRule.isValid(employment_references)
    : true;

  const handleSaveReference = (
    newReference: components["schemas"]["EmploymentReferencesValue"],
    index: number
  ) => {
    const newReferences = employment_references?.references;
    newReferences[index] = newReference;
    saveEntries(newReferences);
  };

  const handleDeleteReference = (index: number) => {
    const newReferences = employment_references?.references?.filter(
      (_, i) => index !== i
    );
    saveEntries(newReferences);
  };

  const saveEntries = async (
    entries: components["schemas"]["EmploymentReferencesValue"][]
  ) => {
    const newEntries = {
      employer_reference_skip_reason: null,
      references: entries,
    };
    setEmploymentReferences(newEntries);
    await helpers.setValue(newEntries); // await required for race condition
    await saveData({ skipValidation: true, hasMultipleEntries: true });
  };

  return (
    <>
      <GridItem>
        <Text as="h2" style="form/section-heading">
          <Trans>My references</Trans>
        </Text>
      </GridItem>

      {employment_references?.references?.length > 0 && (
        <EmployerReferenceList
          field={config}
          references={employment_references?.references}
          onSave={handleSaveReference}
          onDelete={handleDeleteReference}
        />
      )}

      <GridItem
        className={css({
          display: "block",
        })}
      >
        {isMaxReferencesRuleValid && (
          <EmployerReferenceModal
            index={employment_references?.references?.length}
            onSave={handleSaveReference}
            requirePhone={config.require_phone}
            requireEmail={config.require_email}
            trigger={
              <GhostButton
                leftIcon="add"
                size="medium"
                fill="background-base"
                border="border-strong"
                testID="employment-references-add-reference-button"
              >
                <Trans>Add reference</Trans>
              </GhostButton>
            }
          />
        )}
      </GridItem>
    </>
  );
}
